import React, { useEffect, useMemo, useState } from 'react';
import { Layout as LayoutType, Responsive, WidthProvider } from 'react-grid-layout';
import { useLocation } from 'react-router-dom';

import classNames from 'classnames';
import styled from 'styled-components';

import { updatePartialView } from 'apis/ViewAPI';
import { useSidebarMenuLogic } from 'components/layout/hooks';
import { useRootContext } from 'components/layout/RootContext';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { ViewWidget } from 'components/View/types';
import {
  generateSalesBattlecardLayout,
  RESPONSIVE_GRID_LAYOUT_BREAKPOINTS,
  RESPONSIVE_GRID_LAYOUT_COLS,
  RESPONSIVE_GRID_LAYOUT_ROW_HEIGHT,
} from 'utils/helpers/layout';

import { SalesBattlecardContext } from './SalesBattlecardContext';
import SalesBattlecardTabs from './SalesBattlecardTabs';
import { WIDGET_TYPES, WIDGETS_HIDE_ON_SALE_BATTLECARD_MARKETING_OVERVIEW } from '../../common/Widgets/constants';
import Widget from '../../common/Widgets/Widget/Widget';
import { getWidgetFilter } from '../View/utils';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const ResponsiveGridLayout = WidthProvider(Responsive);
const StyledResponsiveGridLayout = styled(ResponsiveGridLayout)`
  &.resizing {
    .react-resizable-handle-se {
      opacity: 1;
    }
  }
`;

const ResponsiveWidgetContainer = styled.div`
  & {
    .react-resizable-handle.react-resizable-handle-se {
      bottom: 5px;
      right: 5px;
      opacity: 0;
    }

    :hover {
      .react-resizable-handle.react-resizable-handle-se {
        opacity: 1;
      }
    }

    .widget-container {
      width: inherit;
      height: inherit;
    }
  }

  &.hidden-widget-slot {
    :hover {
      .react-resizable-handle.react-resizable-handle-se {
        opacity: 0;
      }
    }
  }
`;

const SalesBattlecardContainer = () => {
  const { view, viewId, viewFilters, preview, competitors } = useCommunityViewContext();
  const { showAICompetitiveIntelligence } = useSidebarMenuLogic();
  const { showMessage } = useRootContext();
  const location = useLocation();
  const [selectedCompetitorId, setSelectedCompetitorId] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [layout, setLayout] = useState<LayoutType[]>([]);

  // We want to hide certain widgets from `Sales Battlecard view
  // Currently there is no better way as we don't support widget filtering.
  const allWidgets: ViewWidget[] = view?.widgets ?? [];
  const hiddenIds = new Set(
    showAICompetitiveIntelligence
      ? allWidgets.filter((w) => w.type === WIDGET_TYPES.MARKET_POSITIONING).map((w) => String(w.id))
      : []
  );
  const widgets = allWidgets.filter((w) => !hiddenIds.has(String(w.id)));
  const visibleLayout = layout.filter((item) => !hiddenIds.has(item.i));
  const filteredCompetitors = useMemo(
    () =>
      (viewFilters as any)?.competitors?.length
        ? competitors?.filter((c: any) => (viewFilters as any).competitors.includes(c.id))
        : competitors,
    [competitors, viewFilters]
  );

  useEffect(() => {
    if (view?.layout?.length > 0) {
      setLayout(view.layout);
    } else {
      setLayout(generateSalesBattlecardLayout(view?.widgets ?? []));
    }
  }, [view]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const competitorIdValue = queryParams.get('competitor_id');
    if (!competitorIdValue) return;

    const competitorIdFromQuery = Number(competitorIdValue);
    if (!Number.isInteger(competitorIdFromQuery)) return;

    const hasMatchingCompetitor = filteredCompetitors?.some(
      (competitor: any) => competitor.id === competitorIdFromQuery
    );
    if (!hasMatchingCompetitor) return;

    setSelectedCompetitorId((previousSelectedCompetitorId) => previousSelectedCompetitorId ?? competitorIdFromQuery);
  }, [filteredCompetitors, location.search]);

  const handleTabChange = (competitorId: number | null) => {
    setSelectedCompetitorId(competitorId);
  };

  const handleItemChange = (updatedLayout: LayoutType[]) => {
    if (isResizing) {
      setIsResizing(false);
    }
    const mergedLayout = [...updatedLayout, ...layout.filter((item) => hiddenIds.has(item.i))];
    updatePartialView(viewId, { layout: mergedLayout })
      .then((response) => {
        if (Array.isArray(response?.layout)) {
          setLayout(response.layout);
        }
      })
      .catch(() => showMessage('error', 'Trouble saving layout.'));
  };

  const onResizeStart = () => {
    setIsResizing(true);
  };

  return (
    <SalesBattlecardContext.Provider value={{ selectedCompetitorId }}>
      <Container>
        <SalesBattlecardTabs selectedCompetitorId={selectedCompetitorId} onTabChange={handleTabChange} />
        <StyledResponsiveGridLayout
          key={`${selectedCompetitorId}-resizing`}
          className={classNames('layout', { resizing: isResizing })}
          layouts={{
            xxl: visibleLayout,
            xl: visibleLayout,
            lg: visibleLayout,
            md: visibleLayout,
            sm: visibleLayout,
            xs: visibleLayout,
          }}
          breakpoints={RESPONSIVE_GRID_LAYOUT_BREAKPOINTS}
          cols={RESPONSIVE_GRID_LAYOUT_COLS}
          compactType="vertical"
          rowHeight={RESPONSIVE_GRID_LAYOUT_ROW_HEIGHT}
          margin={[20, 20]}
          isResizable={!preview}
          isDraggable={!preview}
          isDroppable={false}
          style={{ width: '100%' }}
          draggableHandle={'.widget-drag-handle'}
          onDragStop={handleItemChange}
          onResizeStart={onResizeStart}
          onResizeStop={handleItemChange}
        >
          {widgets.map((widget: ViewWidget) => {
            if (!selectedCompetitorId && WIDGETS_HIDE_ON_SALE_BATTLECARD_MARKETING_OVERVIEW.includes(widget.type)) {
              return <ResponsiveWidgetContainer className="hidden-widget-slot" key={`${widget.id}`} />;
            }

            return (
              <ResponsiveWidgetContainer key={`${widget.id}`}>
                <Widget
                  id={widget.id}
                  type={widget.type}
                  name={widget.name}
                  note={widget.note}
                  initial_settings={widget.initial_settings}
                  group_by={widget.group_by}
                  filters={getWidgetFilter(true, viewFilters, widget.filter)}
                />
              </ResponsiveWidgetContainer>
            );
          })}
        </StyledResponsiveGridLayout>
      </Container>
    </SalesBattlecardContext.Provider>
  );
};

export default SalesBattlecardContainer;
